import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { PaymentToRecord } from '@models/models';

@Injectable()
export class InvoicesDataProvider {
	payment: BehaviorSubject<any> = new BehaviorSubject(false);
	invoiceUpdate: BehaviorSubject<any> = new BehaviorSubject(false);

	constructor(private http: HttpClient) {}

	get paymentObservable() {
		return this.payment.asObservable();
	}

	get shouldInvoiceBeUpdatedObservable() {
		return this.invoiceUpdate.asObservable();
	}

	emitPayment(payment) {
		return this.payment.next(Object.assign({}, payment));
	}

	emitInvoiceUpdateEvent(value) {
		return this.invoiceUpdate.next(value);
	}

	destroyInvoiceUpdatedObservable(): void {
		this.invoiceUpdate.next(false);
	}

	sendInvoiceEmail(data, type: string, invoiceId: number): Observable<any> {
		const sendInvoiceUrl = `/protected/rest/invoices/invoice/send/${invoiceId}`;
		const sendReceiptUrl = `/protected/rest/payment-receipt/${invoiceId}/send`;

		const url = type === 'receipt' ? sendReceiptUrl : sendInvoiceUrl;

		const { sendMessageData, storedFiles, uploadedFiles } = data;

		const formData: FormData = new FormData();

		for (const key in sendMessageData) {
			if (key !== 'attachmentsData') {
				formData.append(key, sendMessageData[key]);
			}
		}

		for (const key in storedFiles) {
			formData.append(`storedAttachments[${key}]`, storedFiles[key].id);
		}

		for (const key in uploadedFiles) {
			formData.append(`attachments[${key}]`, uploadedFiles[key]);
		}

		return this.http.post(url, formData);
	}

	getInvoice(id: number, path: string): Observable<any> {
		const paymentsPath = path.includes('payments/payment');
		const url = paymentsPath
			? `/protected/rest/jobs/payments/payment/${id}`
			: `/protected/rest/invoices/${id}`;
		return this.http.get(url);
	}

	getInvoiceV1(id: number): Observable<any> {
		const url = `/api/user/v1/invoice/${id}/get`;
		return this.http.get(url);
	}

	getInvoicePreviewURL(invoiceId: number): Observable<any> {
		const url = `/protected/rest/invoices/invoice/payonline/${invoiceId}`;
		const body = {};
		return this.http.post(url, body);
	}

	undoWriteOff(paymentId: number): Observable<any> {
		const url = `/api/user/payments/${paymentId}/undowriteoff`;
		const body = {};
		return this.http.post(url, body);
	}

	writeOff(paymentId: number): Observable<any> {
		const url = `/api/user/payments/${paymentId}/writeoff`;
		const body = {};
		return this.http.post(url, body);
	}

	deleteInvoice(invoiceId: number): Observable<any> {
		const url = `/protected/rest/jobs/invoices/invoice/delete/${invoiceId}`;
		const body = {};
		return this.http.post(url, body);
	}

	setUnpaid(paymentId: number): Observable<any> {
		const url = `/api/user/payments/${paymentId}/unpaid`;
		const body = {};
		return this.http.post(url, body);
	}

	markAsPaid(payment: PaymentToRecord): Observable<any> {
		const url = `/api/user/payments/${payment.id}/paid`;
		return this.http.post(url, payment);
	}

	fetchSchedule(scheduleId: number): Observable<any> {
		const url = `/protected/rest/invoice/schedule?id=${scheduleId}`;
		return this.http.get(url);
	}

	updateInvoiceV1(id, invoice): Observable<any> {
		const url = `/api/user/v1/invoice/${id}/update`;
		return this.http.put(url, invoice);
	}

	saveInvoice(jobId: number, data): Observable<any> {
		const url = `/protected/rest/job/${jobId}/invoice/save`;
		return this.http.post(url, data);
	}

	// TODO remove after implementing new API
	saveInvoiceNew(data): Observable<any> {
		const url = '/api/user/v1/invoice/create';
		return this.http.post(url, data);
	}

	saveInvoiceV1(data, jobId: number): Observable<any> {
		const url = `/api/user/v1/job/${jobId}/invoice`;
		return this.http.post(url, data);
	}

	saveNewInvoice(jobId: number, data): Observable<Object> {
		const url = `/api/user/v1/job/${jobId}/invoice`;
		return this.http.post(url, data);
	}

	updateInvoice(invoiceId: number, data): Observable<Object> {
		const url = `/api/user/v1/invoice/${invoiceId}/update`;
		return this.http.put(url, data);
	}

	getInvoiceToEdit(
		jobId: number,
		isJob: boolean,
		invoiceId?: number
	): Observable<any> {
		let url = isJob
			? `/protected/rest/invoice/sources?jobId=${jobId}`
			: `/protected/rest/invoice/sources?leadId=${jobId}`;

		if (invoiceId) {
			url += `&invoiceId=${invoiceId}`;
		}

		return this.http.get(url);
	}

	getInvoiceToEditNew(
		jobId: number,
		isJob: boolean,
		invoiceId?: number
	): Observable<any> {
		let url = isJob
			? `/api/user/v1/invoice/source?jobId=${jobId}`
			: `/api/user/v1/invoice/source?leadId=${jobId}`;

		if (invoiceId) {
			url += `&invoiceId=${invoiceId}`;
		}

		return this.http.get(url);
	}

	calcInvoiceSummary(data): Observable<any> {
		const url = '/api/user/uihelper/calcInvoiceSummary';
		return this.http.post(url, data);
	}

	calculateInvoiceSummary(data): Observable<any> {
		const url = '/api/public/v1/ui/helper/calculate/summary';
		return this.http.post(url, data);
	}

	getInvoiceIdAndDate(jobId): Observable<Object> {
		const url = `/api/user/v1/job/${jobId}/invoice/template`;
		return this.http.get(url);
	}

	getInvoiceOverviewV1(jobId: number) {
		const url = `/api/user/v1/job/${jobId}/invoices/overview`;
		return this.http.get(url);
	}

	integrateInvoiceWithQbOrZero(
		jobId: number,
		integrateInvoiceWithQbOrZero: boolean
	) {
		const url = `/api/user/v1/job/${jobId}/shootproof/invoice/import?syncWithAccountSystem=${integrateInvoiceWithQbOrZero}`;
		return this.http.post(url, null);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=nfw9yna8xro!./shared/services/invoice/invoices-data-service/invoices.data.service.ts